import { useMemo } from 'react'
import dayjs from 'dayjs'

import useQuery from '@shared/hooks/src/useQuery'
import { useMe } from '@shared/providers/src/MeProvider'
import API from '@shared/services/src/API'
import { AuthUtils, mapRailsTimezoneToJS, QK } from '@shared/utils'

import useBusinessHours from '@hooks/useBusinessHours'
import useProviderBusinessHoursDisclaimerSeen from '@hooks/useProviderBusinessHoursDisclaimerSeen'
import { format } from '@pages/Calendar/Calendar.utils'
import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

const staleTime = 3 * 60 * 60 * 1000 // data is considered stale only after 3 hours

export default function OutsideOfBusinessHoursPopup() {
  const me = useMe()
  const providerId = me.provider?.id
  const userId = me.id
  const timezone = mapRailsTimezoneToJS(me.timezone)
  const { data: businessHours } = useBusinessHours()
  const [disclaimerSeen, setDisclaimerSeen] = useProviderBusinessHoursDisclaimerSeen()

  const today = dayjs().tz(timezone)
  const todayStart = today.startOf('day').format(format)
  const todayEnd = today.endOf('day').format(format)

  const isOutsideOfBusinessHours = useMemo(() => {
    if (!businessHours || disclaimerSeen) return false

    const now = dayjs().tz(timezone)
    const todayDate = now.format('YYYY-MM-DD')

    const start = dayjs.tz(`${todayDate} ${businessHours.start_time}`, timezone)
    const end = dayjs.tz(`${todayDate} ${businessHours.end_time}`, timezone)

    return !now.isBetween(start, end)
  }, [businessHours, disclaimerSeen, timezone])

  const availabilitiesEnabled = Boolean(providerId) && Boolean(businessHours) && !isOutsideOfBusinessHours && !disclaimerSeen

  const { data: availabilities } = useQuery({
    queryKey: QK.providers.id(providerId).availabilities.list({ start_date: todayStart, end_date: todayEnd }),
    queryFn: () => API.providers.id(providerId).availabilities.list({ start_date: todayStart, end_date: todayEnd }),
    enabled: availabilitiesEnabled,
    staleTime,
  })

  const { data: adminTimes } = useQuery({
    queryKey: QK.users.id(userId).adminTimes.list({ start_date_time: todayStart, end_date_time: todayEnd }),
    queryFn: () => API.users.id(userId).adminTimes.list({ start_date_time: todayStart, end_date_time: todayEnd }),
    enabled: availabilitiesEnabled,
    staleTime,
  })

  const outsideOfAvailability = useMemo(() => {
    if (!availabilities || !adminTimes || disclaimerSeen) return false

    const now = dayjs().tz(timezone)

    const hasAvailability = availabilities?.some((availability) => {
      const start = dayjs(availability.start).tz(timezone)
      const end = dayjs(availability.end).tz(timezone)
      return now.isBetween(start, end, null, '[)')
    })

    const hasAdminTime = adminTimes?.some((adminTime) => {
      const start = dayjs(adminTime.start).tz(timezone)
      const end = dayjs(adminTime.end).tz(timezone)
      return now.isBetween(start, end, null, '[)')
    })

    return !hasAvailability && !hasAdminTime
  }, [availabilities, adminTimes, disclaimerSeen, timezone])

  return (
    <>
      <ConfirmationV2
        preventEasyDismiss
        open={isOutsideOfBusinessHours}
        variant="error"
        title="Outside Business Hours"
        description={`It is currently outside of Q Care's business hours. Please log out and return during our normal business hours of ${dayjs(businessHours?.start_time, 'HH:mm').format('LT')} - ${dayjs(businessHours?.end_time, 'HH:mm').format('LT')}.\nThank you!`}
        confirmLabel="Continue"
        rejectLabel="Log Out"
        onConfirm={() => setDisclaimerSeen(true)}
        onReject={() => AuthUtils.logout()}
      />

      <ConfirmationV2
        preventEasyDismiss
        open={outsideOfAvailability}
        variant="error"
        title="Outside Scheduled Availability"
        description={`You are currently accessing the platform outside of your scheduled availability. Please add availability or log out and return on your next scheduled shift.\nThank you!`}
        confirmLabel="Continue"
        rejectLabel="Log Out"
        onConfirm={() => setDisclaimerSeen(true)}
        onReject={() => AuthUtils.logout()}
      />
    </>
  )
}
